import { useCallback } from 'react'

import { BoxProps } from 'ui/components/Box'
import { FileUploadTriggerStyle } from 'ui/components/FileUpload/FileUpload.css'

import { useFileUploadContext } from './useFileUploadContext'

type UseFileUploadDropzoneStateProps = {
    onClickProp?: BoxProps['onClick']
    onDragOverProp?: BoxProps['onDragOver']
    onDragEnterProp?: BoxProps['onDragEnter']
    onDragLeaveProp?: BoxProps['onDragLeave']
    onDropProp?: BoxProps['onDrop']
    onPasteProp?: BoxProps['onPaste']
    onKeyDownProp?: BoxProps['onKeyDown']
}

export function useFileUploadDropzoneState({
    onClickProp,
    onDragOverProp,
    onDragEnterProp,
    onDragLeaveProp,
    onDropProp,
    onPasteProp,
    onKeyDownProp,
}: UseFileUploadDropzoneStateProps) {
    const { dispatch, inputRef, dropzoneId, inputId, listId, disabled, isDraggingOver } =
        useFileUploadContext()

    const onClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            onClickProp?.(event)

            if (event.defaultPrevented) return

            const target = event.target

            const isFromTrigger =
                target instanceof HTMLElement && target.closest(`.${FileUploadTriggerStyle}`)
            if (isFromTrigger) return

            // Trigger OS file upload dialog.
            inputRef.current?.click()
        },
        [inputRef, onClickProp]
    )

    const onDragOver = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            onDragOverProp?.(event)

            if (event.defaultPrevented) return

            event.preventDefault()
            dispatch({ type: 'SET_IS_DRAGGING_OVER', isDraggingOver: true })
        },
        [dispatch, onDragOverProp]
    )

    const onDragEnter = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            onDragEnterProp?.(event)

            if (event.defaultPrevented) return

            event.preventDefault()
            dispatch({ type: 'SET_IS_DRAGGING_OVER', isDraggingOver: true })
        },
        [dispatch, onDragEnterProp]
    )

    const onDragLeave = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            onDragLeaveProp?.(event)

            if (event.defaultPrevented) return

            const relatedTarget = event.relatedTarget
            if (
                relatedTarget &&
                relatedTarget instanceof Node &&
                event.currentTarget.contains(relatedTarget)
            ) {
                return
            }

            event.preventDefault()
            dispatch({ type: 'SET_IS_DRAGGING_OVER', isDraggingOver: false })
        },
        [dispatch, onDragLeaveProp]
    )

    const onDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            onDropProp?.(event)

            if (event.defaultPrevented) return

            event.preventDefault()
            dispatch({ type: 'SET_IS_DRAGGING_OVER', isDraggingOver: false })

            const files = Array.from(event.dataTransfer.files)
            const inputElement = inputRef.current
            if (!inputElement) return

            const dataTransfer = new DataTransfer()
            for (const file of files) {
                dataTransfer.items.add(file)
            }

            inputElement.files = dataTransfer.files
            // Dispatch change event to trigger React update.
            inputElement.dispatchEvent(new Event('change', { bubbles: true }))
        },
        [dispatch, inputRef, onDropProp]
    )

    const onPaste = useCallback(
        (event: React.ClipboardEvent<HTMLDivElement>) => {
            onPasteProp?.(event)

            if (event.defaultPrevented) return

            event.preventDefault()
            dispatch({ type: 'SET_IS_DRAGGING_OVER', isDraggingOver: false })

            const items = event.clipboardData?.items
            if (!items) return

            const files: File[] = []
            for (let i = 0; i < items.length; i++) {
                const item = items[i]
                if (item?.kind === 'file') {
                    const file = item.getAsFile()
                    if (file) {
                        files.push(file)
                    }
                }
            }

            if (files.length === 0) return

            const inputElement = inputRef.current
            if (!inputElement) return

            const dataTransfer = new DataTransfer()
            for (const file of files) {
                dataTransfer.items.add(file)
            }

            inputElement.files = dataTransfer.files
            // Dispatch change event to trigger React update.
            inputElement.dispatchEvent(new Event('change', { bubbles: true }))
        },
        [dispatch, inputRef, onPasteProp]
    )

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            onKeyDownProp?.(event)

            if (!event.defaultPrevented && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault()
                // Trigger OS file upload dialog.
                inputRef.current?.click()
            }
        },
        [inputRef, onKeyDownProp]
    )

    return {
        onClick,
        onDragOver,
        onDragEnter,
        onDragLeave,
        onDrop,
        onPaste,
        onKeyDown,
        dropzoneId,
        inputId,
        listId,
        disabled,
        isDraggingOver,
    }
}
